import { useTranslation } from '@msdk/common'
import {
  CustomActionDefinition,
  CustomActionPositionToDivider,
  DefaultActionDetailsPositionToDivider,
  DefaultActionType,
  Entity,
  RowActionBehaviour,
} from '@msdk/definitions'
import { ButtonIcon, COLORS, Divider, IconSelector, Menu, styled, Tooltip } from '@msdk/ui'
import React, { useEffect, useState } from 'react'

import { useRowActions } from '../../hooks/useRowActions'
import { ActionElement } from '../FormActions/ActionElement'
import { canActivateRow, canDeactivateRow, canDeleteRow, canEditRow, renderDefaultActions } from './action-checks'
import { useDynamicViewContext } from './context'
import { ActivateRowAction } from './RowActions/ActivateRowAction'
import { DeactivateRowAction } from './RowActions/DeactivateRowAction'
import { DeleteRowAction } from './RowActions/DeleteRowAction'
import { EditRowAction } from './RowActions/EditRowAction'

const MenuIconButton = styled(ButtonIcon)`
  color: ${COLORS.primary.main};
`
interface Props {
  row: Entity
}

export function RowActions({ row }: Props) {
  const t = useTranslation()

  const { viewModel, entityModel, reload, baseMetadata, acl, handleRowHasActions, addOrUpdateFilter, refreshView } =
    useDynamicViewContext()

  const [actionMenuAnchor, setActionMenuAnchor] = useState<HTMLElement | null>(null)

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null)
  }

  const handleOpenActionMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setActionMenuAnchor(e.currentTarget)
  }

  const actionInstances = useRowActions(
    viewModel.rowActions.actions,
    row,
    reload,
    baseMetadata,
    acl,
    addOrUpdateFilter,
    refreshView,
  )

  const onActionClick = (e: Event, m: CustomActionDefinition) => {
    e.preventDefault()
    e.stopPropagation()
    handleCloseActionMenu()
    actionInstances[m.name].onClick()
  }

  const actionsDefault = [
    {
      visible: viewModel.rowActions.behaviour === RowActionBehaviour.Standard && canEditRow(row, acl, viewModel),
      component: (
        <EditRowAction row={row} visualizationMode="MenuItems" key="EditRowAction" onClose={handleCloseActionMenu} />
      ),
      type: DefaultActionType.Edit,
    },

    {
      visible: viewModel.rowActions.behaviour === RowActionBehaviour.Standard && canActivateRow(row, acl),
      component: (
        <ActivateRowAction
          row={row}
          visualizationMode="MenuItems"
          key="ActivateRowAction"
          onClose={handleCloseActionMenu}
        />
      ),
      type: DefaultActionType.Activate,
    },
    {
      visible:
        viewModel.rowActions.behaviour === RowActionBehaviour.Standard && canDeactivateRow(row, acl, entityModel),
      component: (
        <DeactivateRowAction
          row={row}
          visualizationMode="MenuItems"
          key="DeactivateRowAction"
          onClose={handleCloseActionMenu}
        />
      ),
      type: DefaultActionType.Deactivate,
    },
    {
      visible: viewModel.rowActions.behaviour === RowActionBehaviour.Standard && canDeleteRow(row, acl, entityModel),
      component: (
        <DeleteRowAction
          row={row}
          visualizationMode="MenuItems"
          key="DeleteRowAction"
          onClose={handleCloseActionMenu}
        />
      ),
      type: DefaultActionType.Delete,
    },
  ]

  const defaultActions = () => renderDefaultActions(entityModel.defaultActionsDetails, actionsDefault)
  const defaultActionsAboveDivider = () =>
    renderDefaultActions(
      entityModel.defaultActionsDetails.filter(
        (x) => x.positionToDivider === DefaultActionDetailsPositionToDivider.Above,
      ),
      actionsDefault,
    )
  const defaultActionsBelowDivider = () =>
    renderDefaultActions(
      entityModel.defaultActionsDetails.filter(
        (x) => x.positionToDivider === DefaultActionDetailsPositionToDivider.Below,
      ),
      actionsDefault,
    )

  const deleteDeactivateActions = () => {
    const actions = {
      deactivate: {
        visible:
          viewModel.rowActions.behaviour === RowActionBehaviour.Standard && canDeactivateRow(row, acl, entityModel),
        component: (
          <DeactivateRowAction
            row={row}
            visualizationMode="MenuItems"
            key="DeactivateRowAction"
            onClose={handleCloseActionMenu}
          />
        ),
      },
      delete: {
        visible: viewModel.rowActions.behaviour === RowActionBehaviour.Standard && canDeleteRow(row, acl, entityModel),
        component: (
          <DeleteRowAction
            row={row}
            visualizationMode="MenuItems"
            key="DeleteRowAction"
            onClose={handleCloseActionMenu}
          />
        ),
      },
    }

    return Object.values(actions)
      .filter((action) => action.visible)
      .map((action) => action.component)
  }

  const standardActions = () => {
    const actions = {
      edit: {
        visible: viewModel.rowActions.behaviour === RowActionBehaviour.Standard && canEditRow(row, acl, viewModel),
        component: (
          <EditRowAction row={row} visualizationMode="MenuItems" key="EditRowAction" onClose={handleCloseActionMenu} />
        ),
      },
      activate: {
        visible: viewModel.rowActions.behaviour === RowActionBehaviour.Standard && canActivateRow(row, acl),
        component: (
          <ActivateRowAction
            row={row}
            visualizationMode="MenuItems"
            key="ActivateRowAction"
            onClose={handleCloseActionMenu}
          />
        ),
      },
    }

    return Object.values(actions)
      .filter((action) => action.visible)
      .map((action) => action.component)
  }

  const customActions = () =>
    viewModel.rowActions.actions
      .filter((m) => actionInstances[m.name])
      .filter((m) => actionInstances[m.name].isVisible())
      .filter((m) => !actionInstances[m.name].isDisabled())
      .map((m) => (
        <ActionElement
          key={m.name}
          icon={m.icon ?? ''}
          label={t(m.idml, m.description)}
          onClick={(e: Event) => {
            onActionClick(e, m)
            handleCloseActionMenu()
          }}
          disabled={actionInstances[m.name].isDisabled()}
          visualizationMode="MenuItems"
          testId={`action-button-${m.name}`}
          aspect={m.buttonAspect}
          iconAlign={m.iconAlign}
          tooltip={m.tooltip}
          positionToDivider={m.positionToDivider}
        />
      ))

  const customActionsAboveDivider = () =>
    customActions().filter((x) => x.props.positionToDivider === CustomActionPositionToDivider.Above)
  const customActionsBelowDivider = () =>
    customActions().filter((x) => x.props.positionToDivider === CustomActionPositionToDivider.Below)

  const allActions = [...standardActions(), ...customActions(), ...deleteDeactivateActions()]

  const actionsAboveDivider = [
    ...standardActions().filter(
      (action) => !defaultActions().some((defaultAction) => defaultAction.key === action.key),
    ),
    ...defaultActionsAboveDivider(),
    ...customActionsAboveDivider(),
  ]
  const actionsBelowDivider = [
    ...deleteDeactivateActions().filter(
      (action) => !defaultActions().some((defaultAction) => defaultAction.key === action.key),
    ),
    ...defaultActionsBelowDivider(),
    ...customActionsBelowDivider(),
  ]

  useEffect(() => {
    if (handleRowHasActions) {
      handleRowHasActions(row.id, allActions.length > 0)
    }
  }, [actionInstances])

  if (allActions.length === 1) {
    return (
      <span onClick={(e) => e.stopPropagation()}>
        {React.cloneElement(allActions[0], { visualizationMode: 'SingleAction' })}
      </span>
    )
  }

  return (
    <>
      <Tooltip arrow title={t('CMPS_TOOLTIP_MENU_ROW_ICON', 'Menu')}>
        <MenuIconButton onClick={handleOpenActionMenu} disabled={allActions.length === 0}>
          <IconSelector iconName="MoreVert" />
        </MenuIconButton>
      </Tooltip>
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
        onClick={(e) => e.stopPropagation()}
      >
        {actionsAboveDivider}
        {actionsBelowDivider.length > 0 && <Divider />}
        {actionsBelowDivider}
      </Menu>
    </>
  )
}
